import './ShareLink.scss';
import '../../ModifyPermissions/PermissionsSettings/PermissionsSettings.scss';
import * as React from 'react';
import * as PropTypes from 'prop-types';
import stringsResx from '../../Share.resx';
import {
  Mode, ISharingLink, ISharingLinkSettings, ISharingStore, ISharingInformation, ClientId, SharingLinkKind, SharingAudience, SharingRole, ISharingPrincipal
} from '../../../interfaces/SharingInterfaces';
import { IconButton, DefaultButton, IButton } from 'office-ui-fabric-react/lib/Button';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { TextField, ITextField } from 'office-ui-fabric-react/lib/TextField';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { LinkMember } from './LinkMember/LinkMember';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import { getLinkKind, getLinkLabel, getLinkIconName, copySharingLinkToClipboard, ExpirationErrorCode, getDefaultExpirationDateValue, getLinkParticipants } from '../../../utilities/ShareHelper';
import { ShareLinkParticipants } from '../ShareLinkParticipants/ShareLinkParticipants';
import {
  Facepile,
  OverflowButtonType,
  IFacepilePersona
} from 'office-ui-fabric-react/lib/Facepile';
import { ShareLinkDetails } from '../ShareLinkDetails/ShareLinkDetails';
import { PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import { ConfirmationDialog, ConfirmationCode } from '../ShareLink/ConfirmationDialog/ConfirmationDialog';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import { getShortDisplayDate } from '@ms/odsp-utilities/lib/dateTime/DateTime';
import { TooltipHost } from 'office-ui-fabric-react/lib/Tooltip';
import { FocusZone } from 'office-ui-fabric-react/lib/FocusZone';

const MAX_PERSONAS_LINK_MEMBER_FACEPILE = 4;

export interface ISharingLinkProps {
  companyName: string;
  link: ISharingLink;
  clientId: ClientId;
  mode: Mode;
  sharingInformation?: ISharingInformation;
  groupsMemberCount?: number;
  linkRecipients?: Array<IPerson>; // List of recipients that'll receive email with link (not necessarily permissioned).
  enableLinkSettings: boolean;
  showMoreOptions?: boolean;
  onShowLinkDetailsClicked?: (link: ISharingLink) => void;
  onRemoveLink?: () => void;
  linkDetailsView?: boolean;
  showPermissionsList: () => void;
}

export interface ISharingLinkState {
  link?: ISharingLink;
  copyLinkToClipboardSuccess?: boolean;
  allowPasswordChange: boolean;
  linkMemberExpanded: boolean;
  hasSettingsChange: boolean;
  hasPeopleChange: boolean;
  target?: HTMLElement;
  participant?: IPerson;
  linkRemovedText: string;
  participants: IPerson[];
  showConfirmationDialog: boolean;
  confirmationCode: ConfirmationCode;
  selectedPermissions: ISharingLinkSettings;
  expirationErrorCode: ExpirationErrorCode;
  linkDetailsView: boolean;
  participantRemovedText: string;
}

interface ILinkMemberInformation {
  primaryText: string;
  imageUrl: string;
  email: string;
}

export class ShareLink extends React.Component<ISharingLinkProps, ISharingLinkState> {
  private _store: ISharingStore;
  private _textField: ITextField;
  private _copyButton: IButton;
  private _link: ISharingLink;
  private _chevronButton: IButton;

  public refs: {
    [key: string]: React.ReactInstance,
    moreButton: HTMLElement,
  };

  static contextTypes = {
    sharingStore: PropTypes.object.isRequired
  };

  constructor(props: ISharingLinkProps, context: any) {
    super(props);

    this._store = context.sharingStore;
    this._link = this.props.link;

    this.state = {
      linkRemovedText: '',
      copyLinkToClipboardSuccess: false,
      link: this._link,
      allowPasswordChange: false,
      linkMemberExpanded: false,
      hasSettingsChange: false,
      hasPeopleChange: false,
      participants: getLinkParticipants(this._link),
      showConfirmationDialog: false,
      confirmationCode: ConfirmationCode.none,
      selectedPermissions: this._getLinkSettings(),
      expirationErrorCode: ExpirationErrorCode.none,
      linkDetailsView: this.props.linkDetailsView,
      participantRemovedText: '',
    };
  }

  public render(): React.ReactElement<{}> {
    const link = this.props.link;
    const url = link.url;

    const linkIcon = getLinkIconName(this.props.link);
    const linkLabel = getLinkLabel(this.props.link, this.props.companyName, this.props.clientId);
    const hasLinkMembers = (link.principals && link.principals.length > 0) ||
      (link.invitees && link.invitees.length > 0);

    if (this.props.enableLinkSettings && this.state.linkDetailsView) {
      return this._renderLinkDetails();
    }

    return (this.props.enableLinkSettings ?
      <div>
        { this._renderShareLink() }
        { this.props.showMoreOptions && hasLinkMembers && (
          <div
            aria-label={ stringsResx.linkMembersListLabel }
            className='od-ShareLink--inviteesHolder'
            data-is-focusable={ true }
            aria-expanded={ this.state.linkMemberExpanded }
          >
            <div
              className='od-ShareLink--inviteesHolder-header'
              onClick={ this._onChevronClick }
            >
              {
                !this.state.linkMemberExpanded &&
                this._link.sharingLinkKind === SharingLinkKind.flexible &&
                this._renderLinkMembersForEditPanel()
              }
              {
                !!this.state.linkMemberExpanded &&
                <div className='od-ShareLink--inviteesHolder-headerExpanded'>
                  { stringsResx.inviteesHolderLabel }
                </div>
              }
              { this._link.sharingLinkKind === SharingLinkKind.flexible &&
                this._renderChevron()
              }
            </div>
            { this.state.linkMemberExpanded && this._renderParticipantsList() }
            <div className='od-Share-screenReaderOnly' aria-live='assertive' role='alert' aria-label={ this.state.participantRemovedText }>
              { this.state.participantRemovedText }
            </div>
          </div>
        ) }
        { this.state.showConfirmationDialog && this._renderConfirmationDialogDeleteLink() }
      </div>
      :
      <div>
        <div className='od-ShareLink flightOff' >
          <div className='od-ShareLink-main'>
            <div className='od-ShareLink-icon'>
              <Icon iconName={ linkIcon } />
            </div>
            <div className='od-ShareLink-details' aria-live='assertive' aria-relevant='all'>
              <div className='od-Share-screenReaderOnly' aria-live='assertive' role='alert' aria-label={ this.state.linkRemovedText }>
                { this.state.linkRemovedText }
              </div>
              <TextField
                data-is-focusable={ true }
                autoFocus={ true }
                onClick={ this._onLinkClicked }
                className='od-ShareLink-url'
                defaultValue={ url }
                readOnly={ true }
                ariaLabel={ linkLabel }
                componentRef={ (textField) => this._textField = textField }
              />
            </div>
            <IconButton
              data-is-focusable={ true }
              className='od-ShareLink-actionButton'
              onClick={ this._onShareLinkDelete }
              iconProps={ { iconName: 'Cancel' } }
              title={ stringsResx.removeLinkText }
              ariaLabel={ stringsResx.removeLinkText }
            />
          </div>
          <div className='od-ShareLink-description flightOff'>
            { linkLabel }
          </div>
        </div>
        { hasLinkMembers && this._renderLinkMembers() }
      </div>
    );
  }

  @autobind
  private _renderLinkDetails(): JSX.Element {
    return (
      <ShareLinkDetails
        companyName={ this.props.companyName }
        link={ this._link }
        participants={ this.state.participants }
        clientId={ this.props.clientId }
        sharingInformation={ this.props.sharingInformation }
        mode={ this.props.mode }
        groupsMemberCount={ this.props.groupsMemberCount }
        linkRecipients={ this.props.linkRecipients }
        updateLinkSettings={ this._updateLinkSettings }
        getLinkSettings={ this._getLinkSettings }
        selectedPermissions={ this.state.selectedPermissions }
        onRoleChange={ this._onRoleChange }
        onExpirationChange={ this._onExpirationChange }
        onPasswordChange={ this._onPasswordChange }
        onPeoplePickerChange={ this._onPeoplePickerChange }
        hasSettingsChange={ this.state.hasSettingsChange }
        onCancel={ this._onCancel }
        showPermissionsList={ this.props.showPermissionsList }
        enableLinkSettings={ this.props.enableLinkSettings }
        onRemoveLink={ this.props.onRemoveLink }
      />
    );
  }
  private _renderParticipantsList() {
    return (
      <ShareLinkParticipants
        link={ this._link }
        showDetailsPanel={ false }
        participants={ this.state.participants }
        updateLinkSettings={ this._updateLinkSettings }
      />
    );
  }

  private _renderLinkMembersForEditPanel() {
    const linkMemberInformation: IFacepilePersona[] = this.state.participants.map((person: IPerson) => {
      return {
        personaName: person.name,
        imageUrl: person.image
      };
    });

    return (
      <Facepile
        personas={ linkMemberInformation }
        personaSize={ PersonaSize.size24 }
        maxDisplayablePersonas={ MAX_PERSONAS_LINK_MEMBER_FACEPILE }
        overflowButtonType={ OverflowButtonType.descriptive }
        overflowButtonProps={
          { ariaLabel: stringsResx.overflowButtonFacepileAriaLabel }
        }
      />
    );
  }

  private _renderChevron() {
    return (
      <IconButton
        title={ stringsResx.inviteesHolderChevronAriaLabel }
        ariaLabel={ stringsResx.inviteesHolderChevronAriaLabel }
        aria-expanded={ this.state.linkMemberExpanded }
        className='od-ShareLink--inviteesHolder-headerChevron'
        iconProps={ this.state.linkMemberExpanded === true ? { iconName: 'ChevronUp' } : { iconName: 'ChevronDown' } }
        componentRef={ (button) => this._chevronButton = button }
      />
    );
  }

  @autobind
  private _onChevronClick(): void {
    this.setState({ linkMemberExpanded: !this.state.linkMemberExpanded });
  }

  private _renderConfirmationDialogDeleteLink() {
    const { confirmationCode, participants, participant } = this.state;

    return (
      <ConfirmationDialog
        confirmationCode={ confirmationCode }
        participants={ participants }
        participant={ participant }
        onApply={ this._onConfirmationDeleteLink }
        onCancel={ this.state.confirmationCode === ConfirmationCode.back ?
          this._onCancel
          :
          () => {
            this.setState({
              showConfirmationDialog: false,
              confirmationCode: ConfirmationCode.none,
            });
          } }
      />
    );
  }

  @autobind
  private _onCancel(): void {
    this.setState(() => {
      return {
        ...this.state,
        showConfirmationDialog: false,
        participants: getLinkParticipants(this._link),
        expirationErrorCode: ExpirationErrorCode.none,
        selectedPermissions: this._getLinkSettings(),
        hasSettingsChange: false,
        hasPeopleChange: false,
      };
    });

    if (!this.state.hasSettingsChange && !this.state.hasPeopleChange) {
      const clickHandler = this.props.showPermissionsList;
      if (clickHandler) {
        clickHandler();
      }
    }
  }

  @autobind
  private _onConfirmationDeleteLink(): void {
    if (this.state.confirmationCode === ConfirmationCode.link) {
      this._onShareLinkDelete();
      this.setState({
        showConfirmationDialog: false,
        confirmationCode: ConfirmationCode.none,
        hasSettingsChange: false,
        linkDetailsView: false,
      });
    }

    const clickHandler = this.props.showPermissionsList;
    if (clickHandler) {
      clickHandler();
    }
  }

  private _renderShareLink() {
    const link = this._link;
    const url = link.url;
    const { copyLinkToClipboardSuccess } = this.state;

    const linkIcon = getLinkIconName(this.props.link);
    const linkLabel = getLinkLabel(link, this.props.companyName, this.props.clientId);

    return (
      <div className='od-ShareLink' >
        <div className='od-ShareLink-main'>
          <div className={ `od-ShareLink-icon2 icon-${linkIcon}` }>
            <Icon iconName={ linkIcon } aria-hidden={ true } />
          </div>
          <div className='od-ShareLink-details'>
            <TextField
              data-is-focusable={ true }
              className='od-ShareLink-url'
              defaultValue={ url }
              readOnly={ true }
              ariaLabel={ this._getAriaLabel() }
              componentRef={ (textField) => this._textField = textField }
            />
          </div>
          <DefaultButton onClick={ this._copySharingLinkToClipboard } data-is-focusable={ true } ariaLabel={ !copyLinkToClipboardSuccess ? stringsResx.copyLinkLabel : stringsResx.notificationCopiedLabel }
            componentRef={ (button) => this._copyButton = button }>
            { !copyLinkToClipboardSuccess ? stringsResx.copyButtonLabel : stringsResx.copiedButtonLabel }
          </DefaultButton>
          {
            this.props.showMoreOptions ?
              <IconButton
                data-is-focusable={ true }
                className='od-ShareLink-actionButton'
                onClick={ this._showLinkDetails }
                iconProps={ { iconName: 'More' } }
                title={ stringsResx.moreOptionsLabel }
                ariaLabel={ stringsResx.moreOptionsLabel }
              />
              :
              <IconButton
                data-is-focusable={ true }
                title={ stringsResx.removeLinkText }
                className='od-ShareLink-actionButton'
                onClick={ () => this.setState({ showConfirmationDialog: true, confirmationCode: ConfirmationCode.link }) }
                iconProps={ { iconName: 'Cancel' } }
                ariaLabel={ stringsResx.removeLinkText }
              />
          }
        </div>
        <div className='od-ShareLink-description' onClick={ this._showLinkDetails }>
          <div className='od-ShareLink-description-label'>
            <span className='od-ShareHintDetail-statusText'>{ linkLabel }</span>
            { this._renderStatusIcons() }
          </div>
        </div>
      </div>
    );
  }

  private _renderStatusIcons(): JSX.Element {
    const expiration = this._link.expiration;
    const hasPassword = this._link.hasPassword;
    const isBlockDownload = this._link.blockDownload;
    const isCreateOnlyLink = this._link.isCreateOnlyLink;
    let iconData: Array<any> = [];

    if (expiration) {
      if (expiration < new Date(Date.now())) {
        // expired link
        iconData.push({
          icon: 'EventDateMissed12',
          tooltip: `${StringHelper.format(stringsResx.expiredIn2, getShortDisplayDate(expiration))}`
        });
      } else if (expiration.getTime() !== (new Date(0)).getTime()) {
        iconData.push({
          icon: 'Calendar',
          tooltip: `${StringHelper.format(stringsResx.expiresIn2, getShortDisplayDate(expiration))}`
        });
      }
    }

    if (hasPassword) {
      iconData.push({
        icon: 'Lock',
        tooltip: stringsResx.sharePasswordReminderText
      });
    }

    if (isBlockDownload && !isCreateOnlyLink) {
      iconData.push({
        icon: 'Blocked2',
        tooltip: stringsResx.preventDownloadHint
      });
    }

    const iconElements: JSX.Element[] = iconData.map((data: any, index: number) => {
      return (
        <i className={ data.icon !== 'EventDateMissed12' ? 'od-ShareHintDetail-item' : 'od-ShareHintDetail-item od-ShareHintDetail-item-expired' }>
          <TooltipHost content={ data.tooltip } id={ data.icon }>
            <Icon
              iconName={ data.icon }
              aria-describedby={ data.icon }
            />
          </TooltipHost>
        </i>
      );
    });

    return (
      <div className='od-ShareHintDetail-iconsList'>
        { iconElements }
      </div>
    );
  }

  private _getAriaLabel(): string {
    const expiration = this._link.expiration;
    const hasPassword = this._link.hasPassword;
    const isBlockDownload = this._link.blockDownload;
    let ariaLabel = getLinkLabel(this._link, this.props.companyName, this.props.clientId);

    if (expiration) {
      if (expiration < new Date(Date.now())) {
        // expired link
        ariaLabel += ' ' + StringHelper.format(stringsResx.expiredIn2, getShortDisplayDate(expiration)) + '.';

      } else if (expiration.getTime() !== (new Date(0)).getTime()) {
        ariaLabel += ' ' + StringHelper.format(stringsResx.expiresIn2, getShortDisplayDate(expiration)) + '.';
      }
    }

    if (hasPassword) {
      ariaLabel += ' ' + stringsResx.sharePasswordReminderText + '.';
    }

    if (isBlockDownload) {
      ariaLabel += ' ' + stringsResx.preventDownloadHint + '.';
    }

    return ariaLabel;
  }

  private _renderLinkMembers() {
    const linkMemberInformation: ILinkMemberInformation[] = this.props.link.principals.map((principal: ISharingPrincipal) => {
      return {
        primaryText: principal.primaryText,
        imageUrl: principal.imageUrl,
        email: principal.email
      };
    }).concat(this.props.link.invitees.map((person: ISharingPrincipal) => {
      return {
        primaryText: person.primaryText,
        imageUrl: person.imageUrl,
        email: person.email
      };
    }));

    return (
      <FocusZone>
        <ul
          aria-label={ stringsResx.linkMembersListLabel }
          className='od-ShareLink--inviteesHolder flightOff'
          data-is-focusable={ true }
        >
          { linkMemberInformation.map((linkMember: ILinkMemberInformation) => {
            return (
              <li className='od-ShareLink-linkMember' key={ linkMember.primaryText } data-is-focusable={ true } tabIndex={ 0 }>
                <LinkMember
                  primaryText={ linkMember.primaryText }
                  imageUrl={ linkMember.imageUrl }
                />
              </li>
            );
          }) }
        </ul>
      </FocusZone >
    );
  }

  @autobind
  private _showLinkDetails(): void {
    const clickHandler = this.props.onShowLinkDetailsClicked;
    if (clickHandler) {
      clickHandler(this._link);
    }
  }

  @autobind
  private _onShareLinkDelete(): void {
    const link = this._link;
    if (this.props.onRemoveLink) {
      this.props.onRemoveLink();
    }
    this._store.unshareLink(link.sharingLinkKind, link.shareId);
  }

  @autobind
  private _onLinkClicked(eventArgs: any): void {
    try {
      let textField = eventArgs.target;
      textField.focus();
      textField.setSelectionRange(0, textField.value.length);
    } catch (error) {
      // Error handled below.
    }
  }

  @autobind
  private _copySharingLinkToClipboard(): void {
    const { clientId } = this.props;
    const linkUrl: string = this._link.url;
    const textField: ITextField = this._textField;

    copySharingLinkToClipboard(clientId, textField, linkUrl).then(
      (successfullyCopied: boolean) => {
        this.setState({
          copyLinkToClipboardSuccess: successfullyCopied
        });

        this._copyButton.focus();

        if (successfullyCopied) {
          setTimeout(() => {
            this.setState({
              copyLinkToClipboardSuccess: false
            });
          }, 2000);
        }
      }
    );
  }

  @autobind
  private _updateLinkSettings(inviteeToRemove: IPerson): void {
    let inviteesToRemove = [];
    let participantsToAdd = [];
    const participants = this.state.participants;

    if (!inviteeToRemove) {
      // remove an invitee if an invitee is not in the people picker
      for (let invitee of this._link.invitees) {
        let match = [];
        if (invitee.isExternal === true) {
          match = participants.filter(participant => participant.email === invitee.email);
        } else {
          match = participants.filter(participant => participant.userId === invitee.loginName);
        }
        if (match.length === 0) {
          inviteesToRemove.push(invitee);
        }
      }

      // add people if they don't exist
      for (let participant of participants) {
        if (participant.isExternal === true && this.state.selectedPermissions.audience === SharingAudience.organization) { continue; }
        let match = [];
        if (participant.isExternal === true) {
          match = this._link.invitees.filter(invitee => invitee.email === participant.email);
        } else {
          match = this._link.invitees.filter(invitee => invitee.loginName === participant.userId);
        }
        if (match.length === 0 && participant.isResolved === true) {
          participantsToAdd.push(participant);
        }
      }
    } else {
      const findParticipantToRemove = this._link.invitees.filter(invitee => invitee.email === inviteeToRemove.email);
      if (findParticipantToRemove.length > 0) { // ensure the person to remove is on the link, and pass in the ISharingPrincipal.
        inviteesToRemove.push({ ...findParticipantToRemove[0], type: findParticipantToRemove[0].type });

        this.setState({
          participantRemovedText: StringHelper.format(stringsResx.removedPersonText, findParticipantToRemove[0].primaryText)
        });

        if (this._chevronButton) {
          // set focus when person is removed.
          this._chevronButton.focus();
        }
      }
    }

    if (inviteesToRemove.length > 0 || participantsToAdd.length > 0 || this.state.hasSettingsChange) {
      this._store.shareLink({
        settings: this.state.selectedPermissions,
        recipients: participantsToAdd,
        copyLinkCommand: false,
        allRecipientsHaveAccess: false,
        shareId: this._link.shareId,
        inviteesToRemove: inviteesToRemove
      });

      this._store.addListener(() => {
        this.setState(() => {
          let currentLink = this._store.getSharingInformation().sharingLinks.filter(sharingLink => sharingLink.shareId === this._link.shareId);
          if (currentLink && currentLink.length > 0) {
            this._link = currentLink[0];

            return {
              link: this._link,
              participants: this._link.sharingLinkKind === SharingLinkKind.flexible ? getLinkParticipants(this._link) : [],
            };
          }
        });
      });
    }
  }

  @autobind
  private _onPeoplePickerChange(match: Array<IPerson>, pickerInputs: Array<IPerson>) {
    this.setState({
      ...this.state,
      selectedPermissions: this.state.selectedPermissions,
      participants: match.length === 0 ? this.state.participants.concat(pickerInputs) : this.state.participants,
      hasPeopleChange: match.length === 0,
    });
  }

  @autobind
  private _onRoleChange(value: SharingRole): void {
    const selectedPermissions = this.state.selectedPermissions;
    const sharingLinkKind = getLinkKind(selectedPermissions.audience, value === SharingRole.edit);

    this.setState({
      ...this.state,
      selectedPermissions: {
        ...selectedPermissions,
        sharingRole: value,
        isEdit: value === SharingRole.edit,
        sharingLinkKind: sharingLinkKind
      },
      hasSettingsChange: value !== this._getSharingRole() ? true : this.state.hasSettingsChange,
    });
  }

  private _getSharingRole(): SharingRole {
    if (this.state.selectedPermissions.isEdit) {
      return SharingRole.edit;
    } else if (this.state.selectedPermissions.isReview) {
      return SharingRole.review;
    } else {
      return SharingRole.view;
    }
  }

  /**
  * Sets selected date as the expiration. If no expiration is passed (i.e. null),
  * we'll determine the default expiration value depending on tenant settings.
  */
  @autobind
  private _onExpirationChange(expiration: Date) {
    if (!expiration && !this._link.expiration) {
      expiration = getDefaultExpirationDateValue(this.props.sharingInformation.anonymousLinkExpirationRestrictionDays);
    }
    this.setState(() => {
      return {
        ...this.state,
        selectedPermissions: {
          ...this.state.selectedPermissions,
          expiration: expiration,
          shareId: this._link.shareId,
        },
        hasSettingsChange: expiration !== this.state.selectedPermissions.expiration ? true : this.state.hasSettingsChange,
      };
    });
  }

  @autobind
  private _onPasswordChange(newPassword: string) {
    this.setState(() => {
      return {
        ...this.state,
        selectedPermissions: {
          ...this.state.selectedPermissions,
          password: newPassword,
          updatePassword: newPassword.length > 0,
          shareId: this._link.shareId,
        },
        hasSettingsChange: true,
      };
    });
  }

  @autobind
  private _getLinkSettings(): ISharingLinkSettings {
    return {
      allowEditing: true, // todo: Determines whether or not the user can manage an edit link
      audience: this._link.audience,
      expiration: this._link.expiration,
      isEdit: this._link.isEdit,
      isReview: this._link.isReview,
      sharingLinkKind: this._link.sharingLinkKind,
      specificPeople: getLinkParticipants(this._link),
      updatePassword: this._link.updatePassword,
      password: this._link.password,
      sharingRole: this._getSharingRoleFromLink(),
      preventDownload: this._link.blockDownload,
      hasPassword: this._link.hasPassword
    };
  }

  private _getSharingRoleFromLink(): SharingRole {
    if (this._link.isEdit) {
      return SharingRole.edit;
    } else if (this._link.isReview) {
      return SharingRole.review;
    } else {
      return SharingRole.view;
    }
  }
}